import Quill from 'quill';
import {
  convertMarkdownToTextDelta as parseMarkdownAsDelta,
  MARKDOWN_LINK_REGEX,
  MARKDOWN_IMAGE_REGEX,
  MARKDOWN_HEADER_REGEX,
} from 'scaleflow/utils/convert-markdown-to-text-delta';

const Delta = Quill.import('delta');

class MarkdownFormat {
  constructor(quill, options = {}) {
    this.quill = quill;
    this.options = options;
    this.processed = false;
    this.isProcessingUser = false;

    // Process when content is set initially (API)
    this.quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'api' && !this.processed) {
        this.processed = true;
        setTimeout(() => this.processMarkdownContent(), 0);
      } else if (source === 'user' && !this.isProcessingUser) {
        // Check if user just completed markdown syntax
        if (this.shouldProcessUserInput(delta)) {
          setTimeout(() => this.processUserMarkdownContent(), 10);
        }
      }
    });
  }

  shouldProcessUserInput(delta) {
    // Check if the user just completed markdown syntax
    const ops = delta.ops || [];

    for (let op of ops) {
      if (op.insert && typeof op.insert === 'string') {
        // Check for completed bold syntax (**text**)
        if (op.insert.includes('**')) {
          return true;
        }

        // Check for paste operations (large text insertions)
        if (op.insert.length > 20 && this.hasMarkdownSyntax(op.insert)) {
          return true;
        }

        // Check for Enter after potential header
        if (op.insert === '\n') {
          // Get current content to check if previous line looks like a header
          const currentContent = this.quill.getContents();
          const allText = currentContent.ops
            .filter((op) => typeof op.insert === 'string')
            .map((op) => op.insert)
            .join('');

          // Check if text contains header syntax
          if (allText.match(/^#{1,3}\s/m)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  processMarkdownContent() {
    const contents = this.quill.getContents();
    const convertedDelta = this.convertMarkdownToDelta(contents);

    if (convertedDelta) {
      // Update the editor content
      this.quill.setContents(convertedDelta, 'silent');

      // Trigger the onChange callback with the converted delta so it gets saved.
      // Use 'api' (not 'user') so the text-change handler's shouldProcessUserInput
      // branch is never re-entered, preventing an infinite conversion loop.
      setTimeout(() => {
        this.quill.setContents(convertedDelta, 'api');
      }, 50);
    }
  }

  processUserMarkdownContent() {
    if (this.isProcessingUser) return;

    this.isProcessingUser = true;
    const contents = this.quill.getContents();
    const convertedDelta = this.convertMarkdownToDelta(contents);

    if (convertedDelta) {
      // Save current cursor position
      const selection = this.quill.getSelection();

      // Update the editor content
      this.quill.setContents(convertedDelta, 'silent');

      // Restore cursor position
      if (selection) {
        setTimeout(() => {
          this.quill.setSelection(selection, 'silent');
        }, 0);
      }

      // Trigger the onChange callback.
      // Use 'api' (not 'user') so the text-change handler's shouldProcessUserInput
      // branch is never re-entered, preventing an infinite conversion loop.
      setTimeout(() => {
        this.quill.setContents(convertedDelta, 'api');
      }, 50);
    }

    this.isProcessingUser = false;
  }

  convertMarkdownToDelta(originalDelta) {
    const ops = originalDelta.ops || [];
    let hasMarkdown = false;
    const newOps = [];

    for (let op of ops) {
      if (op.insert && typeof op.insert === 'string') {
        const text = op.insert;

        // Check if this text has markdown syntax
        if (this.hasMarkdownSyntax(text)) {
          hasMarkdown = true;
          const processedOps = this.parseMarkdownText(text);
          newOps.push(...processedOps);
        } else {
          newOps.push(op);
        }
      } else {
        newOps.push(op);
      }
    }

    return hasMarkdown ? new Delta(newOps) : null;
  }

  hasMarkdownSyntax(text) {
    return (
      text.includes('**') ||
      MARKDOWN_HEADER_REGEX.test(text) ||
      MARKDOWN_IMAGE_REGEX.test(text) ||
      MARKDOWN_LINK_REGEX.test(text)
    );
  }

  parseMarkdownText(text) {
    const { ops } = parseMarkdownAsDelta(text);
    return ops;
  }
}

export default MarkdownFormat;
